// A barra de um tabuleiro a ser jogado (IDEA-034 v3).
//
// A mesma em todos os jogos, e sempre nos mesmos sítios:
//
//   ← Sair                     ← canto superior esquerdo, sozinho
//   0:42            [ferramentas]  ← relógio à esquerda, ferramentas à direita
//
// Ter isto num sítio só é o que faz os quatro jogos parecerem o mesmo jogo com
// tabuleiros diferentes, em vez de quatro páginas parecidas. E o "Sair" fica
// longe das ferramentas de propósito: as ferramentas usam-se dezenas de vezes,
// o Sair uma vez ou nenhuma, e num telemóvel um botão perigoso encostado a um
// botão frequente acaba por ser carregado.

import { useState } from "react";
import { Link } from "react-router";
import { Confirmacao } from "@/components/ui/Confirmacao";
import { formatarTempo } from "@/components/jogos/RondaCronometrada";

export function BarraDeJogo({
  tempoMs,
  rotulo,
  aoSair,
  children,
}: {
  /**
   * O relógio, ou `null` num jogo que não tem.
   *
   * O Pinpoint não tem, de propósito: premeia pensar bem, não depressa, e um
   * cronómetro à vista empurrava para palpitar à toa. Fica com `rotulo` no
   * lugar — o sítio é o mesmo, o que lá está é que muda.
   */
  tempoMs: number | null;
  /** O que mostrar quando não há relógio (ex.: "2 de 5"). */
  rotulo?: string;
  aoSair: () => void;
  /** As ferramentas do jogo, se tiver. Ficam à direita do relógio. */
  children?: React.ReactNode;
}) {
  return (
    <div className="mb-3 flex w-full max-w-sm flex-col gap-2">
      <button
        type="button"
        onClick={aoSair}
        className="self-start rounded-full px-2 py-1 text-sm font-semibold text-ouro-600 transition-colors hover:bg-creme-200"
      >
        ← Sair
      </button>

      <div className="flex items-center justify-between gap-2">
        {tempoMs !== null ? (
          <span
            role="timer"
            aria-live="off"
            className="font-titulo text-2xl tabular-nums text-tinta-800"
          >
            {formatarTempo(tempoMs)}
          </span>
        ) : (
          <span className="font-titulo text-2xl tabular-nums text-tinta-800">{rotulo}</span>
        )}

        {children ? <div className="flex items-center gap-1.5">{children}</div> : <span />}
      </div>
    </div>
  );
}

/**
 * O "voltar" das páginas de jogo paradas.
 *
 * Mesmo canto que o "Sair" de dentro do tabuleiro, por isso quem já sabe onde
 * ele está não tem de o procurar outra vez. Estava no fundo das páginas, que num
 * telemóvel é depois das regras todas e do histórico todo.
 */
export function VoltarAosJogos() {
  return (
    <Link
      to="/jogos"
      className="mb-4 inline-block rounded-full px-2 py-1 text-sm font-semibold text-ouro-600 transition-colors hover:bg-creme-200"
    >
      ← Jogos
    </Link>
  );
}

/**
 * Sair a meio de um tabuleiro.
 *
 * Duas coisas muito diferentes, conforme quem já jogou:
 *
 *   A outra pessoa **ainda não jogou** — sair é só sair. A ronda continua à
 *   espera das duas e volta-se quando se quiser. Perde-se o que estava no
 *   tabuleiro e o relógio recomeça, que é o que já acontecia ao mudar de
 *   página.
 *
 *   A outra pessoa **já jogou** — sair é desistir, e a ronda fecha com a
 *   vitória dela. Por isso é a única saída que pergunta primeiro: é o botão que
 *   está mais perto do polegar durante um jogo inteiro, e um toque sem querer
 *   não pode custar a ronda.
 *
 * `theyPlayed` é um bit que a API dá de propósito — diz que ela acabou, nunca
 * em quanto tempo (ver o contrato de TimedRoundView).
 */
export function useSaidaDoJogo({
  ronda,
  cronometro,
  submeter,
  aoFechar,
}: {
  ronda: { round: { id: string }; theyPlayed: boolean } | null;
  cronometro: { parar: () => number };
  submeter: (roundId: string, dados: { tempoMs: number; desistiu?: boolean }) => Promise<void>;
  aoFechar: () => void;
}) {
  const [aConfirmar, setAConfirmar] = useState(false);
  const [aDesistir, setADesistir] = useState(false);

  const desistir = Boolean(ronda?.theyPlayed);

  function pedirSaida() {
    if (desistir) setAConfirmar(true);
    else {
      cronometro.parar();
      aoFechar();
    }
  }

  async function confirmarSaida() {
    if (!ronda) return;
    setADesistir(true);
    const tempoMs = cronometro.parar();
    try {
      // O tempo vai à mesma: não decide nada (uma desistência perde sempre) mas
      // deixa o registo dizer quanto tempo se esteve lá.
      await submeter(ronda.round.id, { tempoMs: Math.max(1, tempoMs), desistiu: true });
    } finally {
      setADesistir(false);
      setAConfirmar(false);
      aoFechar();
    }
  }

  return {
    pedirSaida,
    desistir,
    dialogo: (
      <Confirmacao
        aberto={aConfirmar}
        destrutivo
        aProcessar={aDesistir}
        titulo="Desistir da ronda?"
        mensagem="A outra pessoa já jogou este tabuleiro. Se saíres agora, a ronda fecha e a vitória é dela."
        textoConfirmar="Desistir"
        textoCancelar="Continuar a jogar"
        onCancelar={() => setAConfirmar(false)}
        onConfirmar={confirmarSaida}
      />
    ),
  };
}
